import { forwardRef, type Ref, type TextareaHTMLAttributes } from 'react';
import { type FieldErrors, type FieldValues, type Path } from 'react-hook-form';

import FormGroup, { type FormGroupProps } from '../FormGroup/FormGroup';
import { StyledTextareaContainer, StyledTextarea, StyledErrorIcon } from './Textarea.styles';

interface TextareaProps<T extends FieldValues> extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: Path<T>;
  id: string;
  formGroupProps: Omit<FormGroupProps, 'name' | 'htmlFor' | 'required' | 'children'>;
  errors?: FieldErrors<T>;
}

const Textarea = forwardRef(
  <T extends FieldValues>(
    { name, id, required, formGroupProps, errors, ...rest }: TextareaProps<T>,
    ref?: Ref<HTMLTextAreaElement>
  ) => {
    const hasError = Boolean(errors?.[name]?.message);

    return (
      <FormGroup {...formGroupProps} name={name} htmlFor={id} required={required} errors={errors}>
        <StyledTextareaContainer>
          <StyledTextarea
            hasError={hasError}
            id={id}
            required={required}
            name={name}
            {...rest}
            ref={ref}
          />
          {hasError && <StyledErrorIcon name="warning-circle" />}
        </StyledTextareaContainer>
      </FormGroup>
    );
  }
);

export default Textarea;
